import Phaser from 'phaser';

export type ObjectiveType = 'kill_all' | 'destroy_target_building';

export interface Objective {
  id: string;
  type: ObjectiveType;
  label: string;
  completed: boolean;
  marker?: Phaser.GameObjects.GameObject;
}

export class MissionSystem extends Phaser.Events.EventEmitter {
  private scene: Phaser.Scene;
  private objectives: Objective[] = [];
  private hudText!: Phaser.GameObjects.Text;
  private markerGraphics: Phaser.GameObjects.Graphics[] = [];
  private markerTweens: Phaser.Tweens.Tween[] = [];
  private missionComplete: boolean = false;

  // Position of destroy-target building marker (tile centre in world coords)
  private destroyTargetX: number = 0;
  private destroyTargetY: number = 0;
  private destroyTargetTileX: number = 0;
  private destroyTargetTileY: number = 0;
  private groundLayer: Phaser.Tilemaps.TilemapLayer | null = null;


  constructor(scene: Phaser.Scene) {
    super();
    this.scene = scene;
    this.createHud();
  }

  // ── Setup ─────────────────────────────────────────────────────────────────

  /**
   * Define objectives for a mission.
   * Call once during scene create().
   */
  setObjectives(objectives: Omit<Objective, 'completed' | 'marker'>[]): void {
    this.objectives = objectives.map((o) => ({ ...o, completed: false }));
    this.refreshHud();
  }

  /**
   * Tell the system where the destroy-target building tile is.
   * The system will watch for that tile to be removed.
   */
  setDestroyTarget(
    tileX: number,
    tileY: number,
    groundLayer: Phaser.Tilemaps.TilemapLayer,
    tileSize: number
  ): void {
    this.destroyTargetTileX = tileX;
    this.destroyTargetTileY = tileY;
    this.groundLayer = groundLayer;
    this.destroyTargetX = tileX * tileSize + tileSize / 2;
    this.destroyTargetY = tileY * tileSize + tileSize / 2;
    this.createDestroyMarker(this.destroyTargetX, this.destroyTargetY);
  }

  // ── Objective completion ──────────────────────────────────────────────────

  /** Call from GameScene when EnemyManager reports zero live enemies. */
  notifyAllEnemiesDead(): void {
    this.completeObjectivesOfType('kill_all');
  }

  /** Call from GameScene after a rocket explosion has potentially destroyed tiles. */
  checkDestroyTargetObjective(): void {
    if (!this.groundLayer) return;
    const tile = this.groundLayer.getTileAt(this.destroyTargetTileX, this.destroyTargetTileY);
    // Tile was replaced (to index 1) or doesn't exist any more
    if (!tile || tile.index !== 3) {
      this.completeObjectivesOfType('destroy_target_building');
      this.removeDestroyMarkers();
    }
  }

  /** Call from GameScene when a DestructibleBuilding marked as destroy target is destroyed. */
  notifyDestroyTargetDestroyed(): void {
    this.completeObjectivesOfType('destroy_target_building');
    this.removeDestroyMarkers();
  }

  // ── Update ────────────────────────────────────────────────────────────────

  /** Call from GameScene.update() each frame. */
  update(_time: number, _delta: number): void {
    if (this.missionComplete) return;

    if (this.allComplete()) {
      this.missionComplete = true;
      this.refreshHud();
      this.emit('mission_complete');
    }
  }

  // ── HUD ───────────────────────────────────────────────────────────────────

  private createHud(): void {
    const { width } = this.scene.scale;
    this.hudText = this.scene.add
      .text(width - 12, 12, '', {
        fontSize: '13px',
        color: '#ffffff',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 3,
        align: 'right',
      })
      .setOrigin(1, 0)
      .setScrollFactor(0)
      .setDepth(101);
  }

  private refreshHud(): void {
    const lines: string[] = ['── OBJECTIVES ──'];
    for (const obj of this.objectives) {
      const icon = obj.completed ? '✓' : '○';
      const colour = obj.completed ? '#00ff88' : '#ffffff';
      lines.push(`${icon} ${obj.label}`);
      // Phaser Text doesn't support per-line colours in one setText call, so
      // we just use plain text with a ✓ tick for completed items.
      void colour; // colour hint kept for future rich text upgrade
    }
    if (this.missionComplete) {
      lines.push('');
      lines.push('★ MISSION COMPLETE ★');
    }
    this.hudText.setText(lines);
  }

  // ── Marker sprites ────────────────────────────────────────────────────────

  private createDestroyMarker(x: number, y: number): void {
    const g = this.scene.add.graphics().setDepth(80);
    this.markerGraphics.push(g);

    // Draw a red pulsing circle marker
    const drawMarker = (scale: number, alpha: number) => {
      g.clear();
      g.lineStyle(2, 0xff2200, alpha);
      g.strokeCircle(x, y, 20 * scale);
      g.lineStyle(1, 0xff2200, alpha * 0.5);
      g.strokeCircle(x, y, 28 * scale);
      // Cross hairs
      g.lineStyle(1, 0xff2200, alpha);
      g.lineBetween(x - 10, y, x + 10, y);
      g.lineBetween(x, y - 10, x, y + 10);
    };

    drawMarker(1, 1);

    // Pulse tween via a proxy object
    const proxy = { scale: 1, alpha: 1 };
    const tween = this.scene.tweens.add({
      targets: proxy,
      scale: 1.4,
      alpha: 0.4,
      duration: 900,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
      onUpdate: () => {
        drawMarker(proxy.scale, proxy.alpha);
      },
    });
    this.markerTweens.push(tween);
  }

  private removeDestroyMarkers(): void {
    for (const tween of this.markerTweens) {
      tween.stop();
    }
    for (const g of this.markerGraphics) {
      g.destroy();
    }
    this.markerGraphics = [];
    this.markerTweens = [];
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private completeObjectivesOfType(type: ObjectiveType): void {
    let changed = false;
    for (const obj of this.objectives) {
      if (obj.type === type && !obj.completed) {
        obj.completed = true;
        changed = true;
      }
    }
    if (changed) this.refreshHud();
  }

  private allComplete(): boolean {
    return this.objectives.length > 0 && this.objectives.every((o) => o.completed);
  }

  /** Expose a read-only snapshot for briefing screens etc. */
  getObjectives(): Readonly<Objective>[] {
    return this.objectives;
  }

  isMissionComplete(): boolean {
    return this.missionComplete;
  }

  /** Reset state (e.g. when restarting a mission). */
  reset(): void {
    this.missionComplete = false;
    for (const obj of this.objectives) {
      obj.completed = false;
    }
    this.removeDestroyMarkers();
    this.refreshHud();
  }
}
